export const PROVIDER_NAMES = [
  "microsoft",
  "google",
  "okta",
  "onelogin",
  "keycloak",
  "custom",
] as const;
export type ProviderName = (typeof PROVIDER_NAMES)[number];

export const SSO_PROTOCOLS = ["oidc", "saml"] as const;
export type SSOProtocols = (typeof SSO_PROTOCOLS)[number];

const protocolPrefix: Record<SSOProtocols, string> = { oidc: "o", saml: "s" };

// Must match tokenPrefix in nodejs/apps/auth-service/src/plugins/enterprise-sso.ts
const DNS_RECORD_PREFIX = "_stotles-sso-";

export function generateProviderId(
  organizationId: string,
  protocol: SSOProtocols,
  providerName: ProviderName,
): string {
  return `${organizationId}-${protocolPrefix[protocol]}-${providerName}`;
}

export function generateSamlEntityId(organizationId: string, providerName: ProviderName): string {
  return `urn:stotles:saml:${organizationId}:${providerName}`;
}

/** Host part of the TXT record, i.e. the name without the zone. Must stay within the 63-char DNS label limit. */
export function generateDnsRecordHost(providerId: string): string {
  return `${DNS_RECORD_PREFIX}${providerId}`;
}

/** Fully-qualified TXT record name, as the verifier resolves it. */
export function generateDnsRecordName(providerId: string, domain: string): string {
  return `${generateDnsRecordHost(providerId)}.${domain}`;
}

export function extractEmailDomain(email: string): string {
  const parts = email.split("@");
  return parts.length > 1 ? parts[1] : "";
}

const BASE64_RE = /^[A-Za-z0-9+/\s]+=*\s*$/;

export type PemType = "certificate" | "private-key";

const PEM_LABELS: Record<PemType, string> = {
  certificate: "CERTIFICATE",
  "private-key": "PRIVATE KEY",
};

/** Wraps a bare base64 string with a PEM header/footer and line breaks if needed. */
export function wrapBareBase64AsPem(value: string, pemType: PemType): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith("-----")) return trimmed;
  if (!BASE64_RE.test(trimmed)) return trimmed;

  // Likely a bare certificate/private key since we have reached this point
  const label = PEM_LABELS[pemType];
  const raw = trimmed.replace(/\s+/g, "");
  const lines = [`-----BEGIN ${label}-----`];
  for (let i = 0; i < raw.length; i += 64) {
    lines.push(raw.slice(i, i + 64));
  }
  lines.push(`-----END ${label}-----`);
  return lines.join("\n");
}

function derBytesToBase64(bytes: Uint8Array): string {
  // Iterate rather than spread to avoid RangeError on large ArrayBuffers
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

const MAX_PEM_SIZE = 100_000; // 100 KB
type PemReadResult = { ok: true; pem: string } | { ok: false; error: string };

/** Reads a file and validates it as PEM. When `pemType` is set, non-PEM files are auto-converted. */
export async function readPemFile(
  file: File,
  label: string,
  pemType?: PemType,
): Promise<PemReadResult> {
  if (file.size > MAX_PEM_SIZE) {
    return { ok: false, error: `${label} file is too large (max 100KB)` };
  }
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // PEM files always start with "-----" (e.g. "-----BEGIN CERTIFICATE-----")
  const PEM_DASH = 0x2d; // "-"
  const isPem = bytes.length >= 5 && bytes.slice(0, 5).every((b) => b === PEM_DASH);

  if (isPem) {
    return { ok: true, pem: new TextDecoder().decode(bytes).trim() };
  }

  if (pemType) {
    const text = new TextDecoder().decode(bytes).trim();
    const b64 = BASE64_RE.test(text) ? text : derBytesToBase64(bytes);
    return { ok: true, pem: wrapBareBase64AsPem(b64, pemType) };
  }

  return {
    ok: false,
    error: `${label} file does not appear to be PEM-encoded (expected -----BEGIN header)`,
  };
}

/** Opens a file picker, reads the selected file, and validates it as PEM. Returns `null` if no file was selected. */
export function pickAndReadPemFile(
  accept: string[],
  label: string,
  pemType?: PemType,
): Promise<PemReadResult | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept.join(",");
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }
      resolve(readPemFile(file, label, pemType));
    };
    input.oncancel = () => resolve(null);

    input.click();
  });
}
